import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { CONTACT } from '../../lib/contact';
import type { OrderStatusResponse } from '../../lib/types';
import { ShareCard } from '../status/ShareCard';
import { StatusCards } from '../status/StatusCards';
import { isTerminalStatus, statusTitle } from '../status/statusModel';
import { Timeline } from '../status/Timeline';
import '../status/status.css';

const POLL_MS = 60_000;

/**
 * Public order status / confirmation page (screen 1g) — route
 * /status/:orderName?t=<status_token>. Renders STANDALONE: deep-linked from
 * Klaviyo emails with no project state; everything comes from GET /orders.
 * Polls every 60s until the order reaches a terminal status.
 */
export function StatusScreen() {
  const { orderName } = useParams<{ orderName: string }>();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';

  const [order, setOrder] = useState<OrderStatusResponse | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'notfound' | 'error'>('loading');
  const orderRef = useRef<OrderStatusResponse | null>(null);

  const fetchOrder = useCallback(async () => {
    if (!orderName || !token) {
      setState('notfound');
      return;
    }
    try {
      const res = await api.getOrder(orderName, token);
      orderRef.current = res;
      setOrder(res);
      setState('ready');
    } catch (err) {
      if (orderRef.current) return; // keep showing the last good data on poll errors
      if (err instanceof ApiError && err.httpStatus === 404) setState('notfound');
      else setState('error');
    }
  }, [orderName, token]);

  useEffect(() => {
    void fetchOrder();
  }, [fetchOrder]);

  // Poll while the order can still advance.
  useEffect(() => {
    if (state !== 'ready' || !order || isTerminalStatus(order.status)) return;
    const id = setInterval(() => void fetchOrder(), POLL_MS);
    return () => clearInterval(id);
  }, [state, order, fetchOrder]);

  // Demo-mode toast (mock handoff lands here with ?t=demo).
  const [toast, setToast] = useState(api.isMock && token === 'demo');
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(false), 5000);
    return () => clearTimeout(id);
  }, [toast]);

  return (
    <div className="app">
      <header className="hdr">
        <div className="hdr__brand hdr__brand--stacked">
          <span className="hdr__wordmark">Rewind Photo Lab</span>
          <span className="hdr__eyebrow">Print Portal</span>
        </div>
        <div className="hdr__right">
          <span>Order status page (link emailed to you)</span>
        </div>
      </header>
      <main className="app__main">
        {state === 'loading' && (
          <div className="center-screen">
            <div className="spinner" role="status" aria-label="Loading" />
            <p className="center-screen__body">Looking up your order…</p>
          </div>
        )}

        {state === 'notfound' && (
          <div className="center-screen">
            <h1 className="center-screen__title">We couldn&rsquo;t find that order</h1>
            <p className="center-screen__body">
              The link may be incomplete or out of date. Open the newest email from us and
              try again, or get in touch and we&rsquo;ll sort it out.
            </p>
            <p className="center-screen__body">
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a> · {CONTACT.phone}
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className="center-screen">
            <h1 className="center-screen__title">Something went wrong</h1>
            <p className="center-screen__body">
              We couldn&rsquo;t load your order status. Check your connection and refresh, or
              contact us at <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>.
            </p>
            <button type="button" className="btn btn--primary" onClick={() => void fetchOrder()}>
              Try again
            </button>
          </div>
        )}

        {state === 'ready' && order && (
          <div className="ost-page">
            <div className="ost-col">
              <div className="ost-hero">
                <span
                  className={`ost-hero__tick${
                    order.status === 'cancelled' ? ' ost-hero__tick--cancelled' : ''
                  }`}
                  aria-hidden="true"
                >
                  {order.status === 'cancelled' ? '–' : '✓'}
                </span>
                <h1 className="ost-hero__title">{statusTitle(order)}</h1>
                <div className="ost-hero__sub">
                  Order <strong>#{order.order_name.replace(/^#/, '')}</strong> · confirmation sent
                  to your email
                </div>
              </div>

              {order.status === 'cancelled' ? (
                <div className="ost-cancelled">
                  This order was cancelled. If that&rsquo;s unexpected, contact us and we&rsquo;ll
                  make it right.
                </div>
              ) : (
                <Timeline order={order} />
              )}

              <StatusCards order={order} />

              {order.status !== 'cancelled' && (
                <ShareCard orderName={order.order_name} statusToken={token} />
              )}

              <div className="ost-contact">
                Questions? <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a> ·{' '}
                {CONTACT.phone}
              </div>
            </div>
          </div>
        )}
      </main>
      {toast && (
        <div className="ost-toast" role="status">
          Demo mode: this is sample order data
        </div>
      )}
    </div>
  );
}
